import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Modal, Form } from 'react-bootstrap';
import { useMutation, useQueryClient } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars } from '@fortawesome/free-solid-svg-icons';
import MapContainer, { MapFilters } from '../components/map/MapContainer';
import MapSidebar from '../components/map/MapSidebar';
import { useAuth } from '../contexts/AuthContext';
import { showToast } from '../components/common/ToastContainer';
import { sendConnectionRequest } from '../services/userService';
import 'leaflet/dist/leaflet.css';

const MapPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  // State for map filters
  const [filters, setFilters] = useState<MapFilters>({
    professions: {
      technology: true,
      finance: true,
      healthcare: true,
      education: true,
      engineering: true,
      legal: true,
      marketing: true,
      sales: true,
      humanResources: true,
      artsEntertainment: true,
      manufacturing: true,
      retail: true,
      government: true,
      energy: true,
      student: true,
      retired: true,
      other: true
    },
    experienceLevels: {
      junior: true,
      midLevel: true,
      senior: true
    },
    interest: '',
    maxDistance: 100,
    isDistanceFilterEnabled: false
  });
  
  // Mobile sidebar state - Set initial state to true
  const [showMobileSidebar, setShowMobileSidebar] = useState(true);
  
  // Network insights state
  const [sameFieldCount, setSameFieldCount] = useState(0);

  // Modal states
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [connectionRecipient, setConnectionRecipient] = useState<{ id: number; name: string }>({ id: 0, name: '' });
  const [connectionNote, setConnectionNote] = useState('');
  const [isSubmittingConnection, setIsSubmittingConnection] = useState(false);

  // Handle filter changes
  const handleFiltersChange = (newFilters: MapFilters) => {
    setFilters(newFilters);
  };

  // Handle connection button click
  const handleConnect = (userId: number, userName: string) => {
    setConnectionRecipient({ id: userId, name: userName });
    setShowConnectionModal(true);
  };

  // Handle sending a wave
  const handleSendWave = async (userId: number) => {
    try {
      // In a real implementation, this would call an API
      // For now, we'll just show a toast notification
      showToast(`Wave sent successfully!`, 'success');
    } catch (error) {
      console.error('Error sending wave:', error);
      showToast('Failed to send wave. Please try again.', 'danger');
    }
  };

  // --- Connection Request Mutation --- //
  const { 
    mutate: submitConnectionRequestMutate, 
    isLoading: isSendingConnection
  } = useMutation( 
    (variables: { targetUserId: number, note?: string }) => 
      sendConnectionRequest(variables.targetUserId, variables.note),
    {
      onMutate: () => {
        setIsSubmittingConnection(true);
      },
      onSuccess: (data, variables) => {
        if (data.success) {
          setShowConnectionModal(false);
          setConnectionNote('');
          showToast(data.message || `Connection request sent to ${connectionRecipient.name}`, 'success');
          queryClient.invalidateQueries(['connectionStatus', variables.targetUserId]);
        } else {
          showToast(data.message || 'Failed to send connection request', 'danger');
        }
      },
      onError: (error: any) => {
        console.error('Error sending connection request mutation:', error);
        showToast(error.message || 'Failed to send connection request. Please try again.', 'danger');
      },
      onSettled: () => {
        setIsSubmittingConnection(false);
      }
    }
  );

  // Submit connection request form (using mutation)
  const submitConnectionRequest = () => {
    if (!connectionRecipient.id) return;
    submitConnectionRequestMutate({
      targetUserId: connectionRecipient.id,
      note: connectionNote,
    });
  };

  return (
    <div className="map-wrapper">
      {/* Sidebar */}
      <MapSidebar
        filters={filters}
        onFiltersChange={handleFiltersChange}
        showSidebar={showMobileSidebar}
        onCloseMobile={() => setShowMobileSidebar(false)}
      />
      
      {/* Mobile Toggle Button */}
      <Button
        variant="primary"
        className="rounded-circle shadow position-absolute d-lg-none"
        style={{ 
            top: '10px', 
            left: '10px', 
            zIndex: 999 
        }}
        onClick={() => setShowMobileSidebar(!showMobileSidebar)}
      >
        <FontAwesomeIcon icon={faBars} />
      </Button>
      
      {/* Main Map */}
      <MapContainer
        key={user?.id || 'loggedout'}
        filters={filters}
        onSendWave={handleSendWave}
        onConnect={handleConnect}
      />
      
      {/* Connection Request Modal */}
      <Modal
        show={showConnectionModal}
        onHide={() => setShowConnectionModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Connect with {connectionRecipient.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Add a personal note with your connection request:</p>
          <Form>
            <Form.Group>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Hi, I'd like to connect with you..."
                value={connectionNote}
                onChange={(e) => setConnectionNote(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConnectionModal(false)}
            disabled={isSendingConnection}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submitConnectionRequest}
            disabled={isSendingConnection}
          >
            {isSendingConnection ? 'Sending...' : 'Send Request'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MapPage;
